import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listCohorts, createCohort, deleteCohort, computeCohortSummary } from "../../../utils/cohortHelpers"
import { listCourses } from "../../../utils/courseHelpers"

const CohortFormModal = ({ orgId, courses, onClose, onSaved }) => {
  const [name, setName] = useState("")
  const [courseId, setCourseId] = useState("")
  const [description, setDescription] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const courseOptions = [
    { value: "", label: "Select a course..." },
    ...(courses?.map((c) => ({ value: c?.courseId, label: c?.title })) || []),
  ]

  const handleSave = async (e) => {
    e?.preventDefault()
    setError("")
    if (!name?.trim() || !courseId) {
      setError("Cohort name and course are both required")
      return
    }
    setSaving(true)
    const { error: saveError } = await createCohort({ orgId, name, courseId, description })
    setSaving(false)
    if (saveError) {
      setError(saveError?.message || "Could not create cohort")
      return
    }
    onSaved()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">New Cohort</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <Input label="Cohort name" placeholder="e.g. Blockchain Cohort — August 2026" value={name} onChange={(e) => setName(e?.target?.value)} required />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Course</label>
              <Select options={courseOptions} value={courseId} onChange={setCourseId} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Description (optional)</label>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" fullWidth loading={saving}>Create Cohort</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const CohortsPanel = ({ organization }) => {
  const navigate = useNavigate()
  const orgId = organization?.orgId
  const [cohorts, setCohorts] = useState([])
  const [courses, setCourses] = useState([])
  const [summaries, setSummaries] = useState({})
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadData = async () => {
    setLoading(true)
    const [{ cohorts: list }, { courses: courseList }] = await Promise.all([listCohorts(orgId), listCourses(orgId)])
    setCohorts(list || [])
    setCourses(courseList || [])

    const summaryEntries = await Promise.all(
      (list || []).map(async (cohort) => [cohort.cohortId, await computeCohortSummary(orgId, cohort)])
    )
    setSummaries(Object.fromEntries(summaryEntries))
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const courseNameFor = (courseId) => courses?.find((c) => c?.courseId === courseId)?.title

  const handleDelete = (cohortId) => {
    if (!window.confirm("Delete this cohort? Learners keep their progress but lose their cohort assignment.")) return
    setPendingDeleteId(cohortId)
  }

  const confirmDelete = async () => {
    await deleteCohort(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadData()
  }

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{cohorts?.length || 0} cohort{cohorts?.length === 1 ? "" : "s"}</p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>
          New Cohort
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading cohorts...</div>
      ) : cohorts?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="UsersRound" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No cohorts yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Group learners into a cohort for bootcamps, universities, or structured programs.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>New Cohort</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cohorts?.map((cohort) => {
            const summary = summaries[cohort.cohortId] || { total: 0, activePercent: 0, completionPercent: 0 }
            return (
              <Card key={cohort.cohortId} className="p-0 overflow-hidden gap-3 hover:border-accent/40 transition-colors dark:bg-transparent">
                <CardContent className="pt-5 pb-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{cohort.name}</h4>
                    <Badge variant={cohort.status === "archived" ? "warning" : "success"} className="shrink-0">
                      {cohort.status === "archived" ? "Archived" : "Active"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{courseNameFor(cohort.courseId) || "Unknown course"}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{summary.total} learners</span>
                    <span>{summary.activePercent}% active</span>
                    <span>{summary.completionPercent}% completion</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline" size="sm" fullWidth iconName="UsersRound" iconPosition="left"
                      onClick={() => navigate(`/organization-cohort-workspace?orgId=${orgId}&cohortId=${cohort.cohortId}`)}
                    >
                      Manage
                    </Button>
                    <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(cohort.cohortId)} />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {showFormModal && (
        <CohortFormModal orgId={orgId} courses={courses} onClose={() => setShowFormModal(false)} onSaved={() => { setShowFormModal(false); loadData() }} />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this cohort" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default CohortsPanel